import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useUserPreferences } from './UserContext';
import {
  getTimeOfDayPhase,
  isWithinWindow,
  resolveUserTimezone,
  type TimeOfDayBoundary,
  type TimeOfDayPhase,
} from '../utils/timeOfDayPhase';

const TICK_MS = 60_000;

export interface UserTimeContextValue {
  timezone: string;
  now: Date;
  phase: TimeOfDayPhase;
  isWithinWindow: (start: TimeOfDayBoundary, end: TimeOfDayBoundary) => boolean;
}

const UserTimeContext = createContext<UserTimeContextValue | null>(null);

export interface UserTimeProviderProps {
  children: React.ReactNode;
}

export const UserTimeProvider: React.FC<UserTimeProviderProps> = ({ children }) => {
  const { preferences } = useUserPreferences();
  const timezone = resolveUserTimezone(preferences.timezone);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    setNow(new Date());
    const id = window.setInterval(() => setNow(new Date()), TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const phase = useMemo(() => getTimeOfDayPhase(now, timezone), [now, timezone]);

  const checkWindow = useCallback(
    (start: TimeOfDayBoundary, end: TimeOfDayBoundary) =>
      isWithinWindow(now, timezone, start, end),
    [now, timezone]
  );

  const value = useMemo(
    () => ({
      timezone,
      now,
      phase,
      isWithinWindow: checkWindow,
    }),
    [timezone, now, phase, checkWindow]
  );

  return (
    <UserTimeContext.Provider value={value}>{children}</UserTimeContext.Provider>
  );
};

function useUserTimeContext(): UserTimeContextValue {
  const ctx = useContext(UserTimeContext);
  if (!ctx) {
    throw new Error('useUserTimeContext must be used within UserTimeProvider');
  }
  return ctx;
}

export function useUserLocalNow(): Date {
  return useUserTimeContext().now;
}

export function useTimeOfDayPhase(): TimeOfDayPhase {
  return useUserTimeContext().phase;
}

export function useUserTimezone(): string {
  return useUserTimeContext().timezone;
}

export { useUserTimeContext };
